import { useEffect, useState, useRef } from 'react';
import { notify, requestPermission } from './notification';
import { getNextMovement, type Movement } from './movements';
import type { TimerConfig } from './config';
import { loadSession, saveSession } from './session';

export type TimerStatus = "idle" | "working" | "paused" | "short_break" | "movement_break";

export default function useTimer(config: TimerConfig) {
	const [status, setStatus] = useState<TimerStatus>("idle");
	const session = loadSession()
	const [currentPomodoro, setCurrentPomodoro] = useState(session.currentPomodoro);
	const [deskPosition, setDeskPosition] = useState<"sit" | "stand">(session.deskPosition);
	const [secondsRemaining, setSecondsRemaining] = useState(config.durationMins * 60);
	const [currentMovement, setCurrentMovement] = useState<Movement | undefined>();

	const configRef = useRef(config);
	const currentPomodoroRef = useRef(currentPomodoro);
	const currentMovementRef = useRef(currentMovement);
	const deskPositionRef = useRef(deskPosition);

	useEffect(() => {
		configRef.current = config;
	}, [config]);

	useEffect(() => {
		currentPomodoroRef.current = currentPomodoro;
	}, [currentPomodoro]);

	useEffect(() => {
		currentMovementRef.current = currentMovement;
	}, [currentMovement]);

	useEffect(() => {
		deskPositionRef.current = deskPosition;
	}, [deskPosition]);

	useEffect(() => {
	  saveSession({
		currentPomodoro,
		deskPosition,
		lastUpdated: new Date().toISOString(),
	  });
	}, [currentPomodoro, deskPosition]);

	function isTimerPaused() {
		return status !== "working" && status !== "short_break" && status !== "movement_break";
	}

	// Timer effect
	useEffect(() => {
		if (isTimerPaused()) {
			return;
		}

		const interval = setInterval(() => {
			setSecondsRemaining(prev => {
				if (prev <= 1) {
					if (status === "working") {
						const nextPomodoro = currentPomodoroRef.current + 1;
						setCurrentPomodoro(nextPomodoro);
						setDeskPosition(d => d === "sit" ? "stand" : "sit");

						if (nextPomodoro % configRef.current.movementBreakFrequency === 0) {
							setStatus("movement_break");
							const movement = getNextMovement(configRef.current.movements, currentMovementRef.current);
							setCurrentMovement(movement);
							notify(movement.name, movement.description, !configRef.current.soundEnabled, 'movement');
							return configRef.current.movementBreakMins * 60;
						}

						setStatus("short_break");
						notify("Chill out", "do what ever you want for a little bit", !configRef.current.soundEnabled, 'break');
						return configRef.current.shortBreakMins * 60;
					} else {
						// finishing a break...
						setStatus("working");
						notify("Back to work", `Next block: ${deskPositionRef.current}`, !configRef.current.soundEnabled, 'focus');
						return configRef.current.durationMins * 60;
					}
				}
				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(interval);
	}, [status]);


	function start() {
		requestPermission().then(() => { }).catch(e => console.error(e));
		setStatus("working");
	}

	function pause() {
		setStatus("paused");
	}

	function resume() {
		setStatus("working");
	}

	function reset() {
		setStatus("idle");
		setSecondsRemaining(config.durationMins * 60);
		setCurrentPomodoro(0);
		setDeskPosition("sit");
	}

	function markMovementDone() {
		setStatus("working");
		setSecondsRemaining(config.durationMins * 60);
	}

	return {
		config, start, pause, resume, reset, markMovementDone,
		status, currentPomodoro, deskPosition, secondsRemaining,
		currentMovement,
	};
}


